/* The HTTP state each run stream falls back to (`openRunSocket`'s `poll`).
   They only read; the hook delivers what they return. A 401 here goes
   through apiShim like any call, which sends the user to the login page. */

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

async function readJson(url) {
  const res = await fetch(url);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
  return body?.data;
}

/* A generation (WS /api/ws/generate/{id}): the server keeps every event but
   frames, in order, and serves the rest from the position given. */
export function generationEventsPoll(sessionId) {
  return async ({ delivered }) => {
    const data = await readJson(
      `${API_BASE_URL}features/sessions/${encodeURIComponent(sessionId)}/events?after=${delivered}`,
    );
    return { events: data?.events || [], done: Boolean(data?.done) };
  };
}

/* A trial run (WS /api/ws/run/{id}), read by its own run_id
   (`GET runs/{id}`): its steps recorded so far, then its result. Each read
   is handed on as a `run_state` event (the steps, to set as they are: a
   screen that already had some live sets them again, never counts them
   twice), and once the run has a result, as the run's own `done` event
   (`via: "http"`), so the screen ends it the same way. A run that ended
   without a result (`status` "unknown") is lost: no result is made up. An
   unknown run (another user's, or the server restarted) is a 404, which the
   hook counts as a failed read until it gives up. */
export function runStatePoll(runId) {
  return async () => {
    const run = await readJson(`${API_BASE_URL}runs/${encodeURIComponent(runId)}`);
    const events = [{ type: "run_state", steps: run?.steps || [], paused: Boolean(run?.paused), via: "http" }];
    if (!run?.done) return { events, done: false };
    if (run.status === "unknown") return { events, done: true, lost: true };
    events.push({
      type: "done",
      status: run.status,
      passed: run.passed ?? 0,
      failed: run.failed ?? 0,
      total: run.total ?? 0,
      ...(run.error ? { error: run.error } : {}),
      via: "http",
    });
    return { events, done: true };
  };
}

const RUN_CONTROLS = new Set(["cancel", "stop", "pause", "resume"]);

/* Stop, Pause and Resume of a trial run over HTTP (`POST runs/{id}/control`),
   for `openRunSocket`'s `sendFallback`: while its socket is down they still
   reach the run. Anything else (a typed value) can only go over the socket. */
export function runControl(runId) {
  return (msg) => {
    if (!RUN_CONTROLS.has(msg?.type)) return false;
    fetch(`${API_BASE_URL}runs/${encodeURIComponent(runId)}/control`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: msg.type }),
    }).catch(() => {});
    return true;
  };
}

/* An App Page run (WS /api/vision/ws/{id}): its row says "running" until it
   ends. `onRun` gets the row each time (the screens read its summary). */
export function appPageRunPoll(runId, onRun) {
  return async () => {
    const run = await readJson(`${API_BASE_URL}app-pages/runs/${encodeURIComponent(runId)}`);
    onRun?.(run);
    return { done: Boolean(run) && run.status !== "running" };
  };
}

/* A `run_state` step's status as the screens' step rows name it. A step the
   history marks skipped never ran: it shows as not run (pending). */
export function runStepStatus(status) {
  return status === "passed" || status === "failed" ? status : "pending";
}
